import { Injectable, inject, DestroyRef } from '@angular/core';
import { EventBusService, EventPayload } from 'src/app/shared/services/event-bus.service';
import { LocumsMarginCalculatorEvents } from '../enums/locums-margin-calculator-events.enum';
import { MarginExpensesStore } from './margin-expenses.store';
import { MarginExpenseTypes } from '../../enums/margin-expense-types.enum';
import { MarginExpenseTypesAttributes } from 'src/app/recruiting/locums-margin-calculator/enums/margin-expense-types-attributes.enum';
import { MarginExpense } from 'src/app/recruiting/locums-margin-calculator/models/margin-expense.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { marginExpenseTypeToPaymentType } from 'src/app/recruiting/shared/utilities/margin-mapping';
import { FacilityExpenseCaps } from 'src/app/shared/models/facility-expense-caps.model';
import {
    MarginExpenseReimbursementTypes,
    MarginExpenseInternalCostTypes
} from '../../helpers/margin-expenses-constants';
import { LocumsMarginExpenseAs } from '../enums/locums-margin-expense-as.enum';

@Injectable({ providedIn: 'root' })
export class MarginExpensesReducer {
    private readonly eventBus = inject(EventBusService);
    private readonly marginExpensesStore = inject(MarginExpensesStore);
    private readonly destroy$ = inject(DestroyRef);

    constructor() {
        this.listenToMarginExpenseEvents();
        this.listenToExpenseFieldsEvents();
        this.listenToLoadMarginDetailsEvent();
    }

    private listenToLoadMarginDetailsEvent() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_CALCULATOR_LOAD_MARGIN_DETAILS)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpensesFromData(event.payload.marginDetails.marginExpenses);
            });
    }

    private listenToMarginExpenseEvents() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_ADDED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                const marginExpenseTypeId = event.payload.marginExpenseTypeId as MarginExpenseTypes;
                const marginExpenses = event.payload.marginExpenses as MarginExpense[];
                const expenseCaps = event.payload.expenseCaps as FacilityExpenseCaps[];
                const marginId = event.payload.marginId as number;
                const expensePayload = this.getExpensePayloadFor(marginExpenseTypeId, marginId, marginExpenses);
                this.setExpenseCaps(expensePayload, expenseCaps);
                this.setExpenseAsAttribute(expensePayload, marginExpenseTypeId, marginId);

                this.marginExpensesStore.addMarginExpense(expensePayload);
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_REMOVED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                const marginExpenseTypeId = event.payload.marginExpenseTypeId as MarginExpenseTypes;
                const marginExpenses = event.payload.marginExpenses as MarginExpense[];
                const rowId = event.payload.rowId as number;

                if (
                    marginExpenses?.some(
                        (expense) =>
                            expense.marginExpenseTypeId === marginExpenseTypeId &&
                            expense.marginExpenseDetailId !== undefined &&
                            expense.rowId === rowId
                    )
                ) {
                    this.marginExpensesStore.updateMarginExpenseFieldsForDelete(marginExpenseTypeId, true, rowId);
                } else {
                    this.marginExpensesStore.removeMarginExpense(rowId);
                }
            });
    }

    private updateAllExpenseAttributes(marginExpenseTypeId: MarginExpenseTypes, isDeleted: boolean, rowId: number) {
        const attributes = this.attributeVisibility[marginExpenseTypeId];
        if (!attributes) {
            return;
        }
        for (const attribute of attributes) {
            this.marginExpensesStore.updateMarginExpenseField({
                marginExpenseTypeId: marginExpenseTypeId,
                marginExpenseAttributeId: attribute,
                isDeleted: isDeleted,
                rowId: rowId
            });
        }
    }

    private listenToExpenseFieldsEvents() {
        this.listenToHousingFieldsEvents();
        this.listenToMileageReimbursementFieldsEvents();
        this.listenToLicenseFieldsEvents();
        this.listenToDEAFieldsEvents();
        this.listenToReferralBonusFieldsEvents();
        this.listenToPatientCompensationFundFieldsEvents();
        this.listenToExpenseAllowanceFieldsEvents();
        this.listenToBaggageFeesFieldsEvents();
        this.listenToAirfareFieldsEvents();
        this.listenToRentalCarFieldsEvents();
        this.listenToOtherFieldsEvents();
    }

    private listenToHousingFieldsEvents() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_HOUSING_ISBILLABLE_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.HOUSING,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.IsBillable,
                    value: this.convertToStringOrNull(event.payload.isBillable),
                    rowId: event.payload.rowId as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_HOUSING_NUMBER_OF_NIGHTS_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.HOUSING,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.Nights,
                    value: this.convertToStringOrNull(event.payload.numberOfNights),
                    rowId: event.payload.rowId as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_HOUSING_NIGHTLY_COST_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.HOUSING,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.NightlyCost,
                    value: this.convertToStringOrNull(event.payload.nightlyCost),
                    rowId: event.payload.rowId as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_HOUSING_TOTAL_COST_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.HOUSING,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.TotalCost,
                    value: this.convertToStringOrNull(event.payload.totalCost),
                    rowId: event.payload.rowId as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_HOUSING_EXPENSE_CAP_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.HOUSING,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.ExpenseCap,
                    value: this.convertToStringOrNull(event.payload.expenseCap),
                    rowId: event.payload.rowId as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_HOUSING_EXPENSEAS_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.HOUSING,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.ExpenseAs,
                    value: this.convertToStringOrNull(event.payload.expenseAs),
                    rowId: event.payload.rowId as number
                });
            });
    }

    private listenToMileageReimbursementFieldsEvents() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_MILEAGE_REIMBURSEMENT_ISBILLABLE_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.MILEAGE_REIMBURSEMENT,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.IsBillable,
                    value: this.convertToStringOrNull(event.payload.isBillable),
                    rowId: event.payload.rowId as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_MILEAGE_REIMBURSEMENT_AMOUNT_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.MILEAGE_REIMBURSEMENT,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.Amount,
                    value: this.convertToStringOrNull(event.payload.amount),
                    rowId: event.payload.rowId as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_MILEAGE_REIMBURSEMENT_DESIGNATION_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.MILEAGE_REIMBURSEMENT,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.Designation,
                    value: this.convertToStringOrNull(event.payload.designation),
                    rowId: event.payload.rowId as number
                });
            });
    }

    private listenToLicenseFieldsEvents() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_LICENSE_ISBILLABLE_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.LICENSE,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.IsBillable,
                    value: this.convertToStringOrNull(event.payload.isBillable),
                    rowId: event.payload.rowId as number
                });
            });
        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_LICENSE_AMOUNT_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.LICENSE,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.Amount,
                    value: this.convertToStringOrNull(event.payload.amount),
                    rowId: event.payload.rowId as number
                });
            });
        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_LICENSE_EXPENSEAS_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.LICENSE,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.ExpenseAs,
                    value: this.convertToStringOrNull(event.payload.expenseAs),
                    rowId: event.payload.rowId as number
                });
            });
    }

    private listenToDEAFieldsEvents() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_DEA_ISBILLABLE_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.DEA,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.IsBillable,
                    value: this.convertToStringOrNull(event.payload.isBillable),
                    rowId: event.payload.rowId as number
                });
            });
        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_DEA_AMOUNT_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.DEA,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.Amount,
                    value: this.convertToStringOrNull(event.payload.amount),
                    rowId: event.payload.rowId as number
                });
            });
        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_DEA_EXPENSEAS_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.DEA,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.ExpenseAs,
                    value: this.convertToStringOrNull(event.payload.expenseAs),
                    rowId: event.payload.rowId as number
                });
            });
    }

    private listenToReferralBonusFieldsEvents() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_REFERRAL_BONUS_ISBILLABLE_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.REFERRAL_BONUS,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.IsBillable,
                    value: this.convertToStringOrNull(event.payload.isBillable),
                    rowId: event.payload.rowId as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_REFERRAL_BONUS_AMOUNT_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.REFERRAL_BONUS,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.Amount,
                    value: this.convertToStringOrNull(event.payload.amount),
                    rowId: event.payload.rowId as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_REFERRAL_BONUS_RECIPIENT_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.REFERRAL_BONUS,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.Recipient,
                    value: this.convertToStringOrUndefined(event.payload.id),
                    name: this.convertToStringOrUndefined(event.payload.name),
                    rowId: event.payload.rowId as number
                });
            });
    }

    private listenToPatientCompensationFundFieldsEvents() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_PATIENT_COMPENSATION_FUND_ISBILLABLE_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.PATIENT_COMPENSATION_FUND,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.IsBillable,
                    value: this.convertToStringOrNull(event.payload.isBillable),
                    rowId: event.payload.rowId as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_PATIENT_COMPENSATION_FUND_AMOUNT_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.PATIENT_COMPENSATION_FUND,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.Amount,
                    value: this.convertToStringOrNull(event.payload.amount),
                    rowId: event.payload.rowId as number
                });
            });
    }

    private listenToExpenseAllowanceFieldsEvents() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_EXPENSE_ALLOWANCE_ISBILLABLE_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.EXPENSE_ALLOWANCE,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.IsBillable,
                    value: this.convertToStringOrNull(event.payload.isBillable),
                    rowId: event.payload.rowId as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_EXPENSE_ALLOWANCE_AMOUNT_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.EXPENSE_ALLOWANCE,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.Amount,
                    value: this.convertToStringOrNull(event.payload.amount),
                    rowId: event.payload.rowId as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_EXPENSE_ALLOWANCE_CADENCE_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.EXPENSE_ALLOWANCE,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.Cadence,
                    value: this.convertToStringOrNull(event.payload.cadence),
                    rowId: event.payload.rowId as number
                });
            });
    }

    private listenToAirfareFieldsEvents() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_AIRFARE_ISBILLABLE_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.AIRFARE,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.IsBillable,
                    value: this.convertToStringOrNull(event.payload.isBillable),
                    rowId: event.payload.rowId as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_AIRFARE_NUMBER_OF_ROUND_TRIPS_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.AIRFARE,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.RoundTripFlights,
                    value: this.convertToStringOrNull(event.payload.numberOfRoundTripFlights),
                    rowId: event.payload.rowId as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_AIRFARE_AMOUNT_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.AIRFARE,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.Amount,
                    value: this.convertToStringOrNull(event.payload.amount),
                    rowId: event.payload.rowId as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_AIRFARE_TOTAL_COST_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.AIRFARE,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.TotalCost,
                    value: this.convertToStringOrNull(event.payload.totalCost),
                    rowId: event.payload.rowId as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_AIRFARE_EXPENSE_CAP_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.AIRFARE,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.ExpenseCap,
                    value: this.convertToStringOrNull(event.payload.expenseCap),
                    rowId: event.payload.rowId as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_AIRFARE_EXPENSEAS_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.AIRFARE,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.ExpenseAs,
                    value: this.convertToStringOrNull(event.payload.expenseAs),
                    rowId: event.payload.rowId as number
                });
            });
    }

    private listenToRentalCarFieldsEvents() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_RENTAL_CAR_ISBILLABLE_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.RENTAL_CAR,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.IsBillable,
                    value: this.convertToStringOrNull(event.payload.isBillable),
                    rowId: event.payload.rowId as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_RENTAL_CAR_NUMBER_OF_DAYS_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.RENTAL_CAR,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.Days,
                    value: this.convertToStringOrNull(event.payload.numberOfDays),
                    rowId: event.payload.rowId as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_RENTAL_CAR_DAILY_COST_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.RENTAL_CAR,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.DailyCost,
                    value: this.convertToStringOrNull(event.payload.dailyCost),
                    rowId: event.payload.rowId as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_RENTAL_CAR_TOTAL_COST_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.RENTAL_CAR,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.TotalCost,
                    value: this.convertToStringOrNull(event.payload.totalCost),
                    rowId: event.payload.rowId as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_RENTAL_CAR_EXPENSE_CAP_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.RENTAL_CAR,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.ExpenseCap,
                    value: this.convertToStringOrNull(event.payload.expenseCap),
                    rowId: event.payload.rowId as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_RENTAL_CAR_EXPENSEAS_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.RENTAL_CAR,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.ExpenseAs,
                    value: this.convertToStringOrNull(event.payload.expenseAs),
                    rowId: event.payload.rowId as number
                });
            });
    }

    private listenToBaggageFeesFieldsEvents() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_BAGGAGE_FEES_ISBILLABLE_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.BAGGAGE_FEES,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.IsBillable,
                    value: this.convertToStringOrNull(event.payload.isBillable),
                    rowId: event.payload.rowId as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_BAGGAGE_FEES_AMOUNT_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.BAGGAGE_FEES,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.Amount,
                    value: this.convertToStringOrNull(event.payload.amount),
                    rowId: event.payload.rowId as number
                });
            });
    }

    private listenToOtherFieldsEvents() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_OTHER_SPECIFICATION_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.OTHER,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.Specification,
                    value: this.convertToStringOrNull(event.payload.specification),
                    rowId: event.payload.rowId as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_OTHER_ISBILLABLE_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.OTHER,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.IsBillable,
                    value: this.convertToStringOrNull(event.payload.isBillable),
                    rowId: event.payload.rowId as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_OTHER_AMOUNT_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.OTHER,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.Amount,
                    value: this.convertToStringOrNull(event.payload.amount),
                    rowId: event.payload.rowId as number
                });
            });
        this.eventBus
            .on(LocumsMarginCalculatorEvents.EXPENSE_OTHER_EXPENSEAS_CHANGED)
            .pipe(takeUntilDestroyed(this.destroy$))
            .subscribe((event: EventPayload) => {
                this.marginExpensesStore.updateMarginExpenseField({
                    marginExpenseTypeId: MarginExpenseTypes.OTHER,
                    marginExpenseAttributeId: MarginExpenseTypesAttributes.ExpenseAs,
                    value: this.convertToStringOrNull(event.payload.expenseAs),
                    rowId: event.payload.rowId as number
                });
            });
    }

    private convertToStringOrNull(value: string | number | boolean | null | undefined): string | null {
        return value != null ? String(value) : null;
    }

    private convertToStringOrUndefined(value: string | number | boolean | null | undefined): string | undefined {
        return value != null ? String(value) : undefined;
    }

    private getNewExpenseRowId(marginExpenses: MarginExpense[]): number {
        const maxRowId = Math.max(...marginExpenses.map((e) => e.rowId), 0);
        return maxRowId + 1;
    }

    private getExpensePayloadFor(
        marginExpenseTypeId: MarginExpenseTypes,
        marginId: number,
        marginExpenses: MarginExpense[]
    ): MarginExpense[] {
        const payload: MarginExpense[] = [];
        const newRowId = this.getNewExpenseRowId(marginExpenses);

        const addAttributeToPayload = (attributeId: MarginExpenseTypesAttributes) => {
            payload.push({
                marginExpenseTypeId: marginExpenseTypeId,
                marginExpenseAttributeId: attributeId,
                marginId: marginId,
                value: undefined,
                isDeleted: false,
                rowId: newRowId
            });
        };

        const attributes = this.attributeVisibility[marginExpenseTypeId];
        if (!attributes) {
            return payload;
        }

        for (const attribute of attributes) {
            addAttributeToPayload(attribute);
        }

        return payload;
    }

    private readonly attributeVisibility: Record<MarginExpenseTypes, MarginExpenseTypesAttributes[]> = {
        [MarginExpenseTypes.AIRFARE]: [
            MarginExpenseTypesAttributes.IsBillable,
            MarginExpenseTypesAttributes.RoundTripFlights,
            MarginExpenseTypesAttributes.ExpenseAs,
            MarginExpenseTypesAttributes.Amount,
            MarginExpenseTypesAttributes.TotalCost,
            MarginExpenseTypesAttributes.ExpenseCap
        ],
        [MarginExpenseTypes.RENTAL_CAR]: [
            MarginExpenseTypesAttributes.IsBillable,
            MarginExpenseTypesAttributes.Days,
            MarginExpenseTypesAttributes.ExpenseAs,
            MarginExpenseTypesAttributes.DailyCost,
            MarginExpenseTypesAttributes.TotalCost,
            MarginExpenseTypesAttributes.ExpenseCap
        ],
        [MarginExpenseTypes.HOUSING]: [
            MarginExpenseTypesAttributes.IsBillable,
            MarginExpenseTypesAttributes.Nights,
            MarginExpenseTypesAttributes.ExpenseAs,
            MarginExpenseTypesAttributes.NightlyCost,
            MarginExpenseTypesAttributes.TotalCost,
            MarginExpenseTypesAttributes.ExpenseCap
        ],
        [MarginExpenseTypes.MILEAGE_REIMBURSEMENT]: [
            MarginExpenseTypesAttributes.IsBillable,
            MarginExpenseTypesAttributes.Amount,
            MarginExpenseTypesAttributes.Designation
        ],
        [MarginExpenseTypes.LICENSE]: [
            MarginExpenseTypesAttributes.IsBillable,
            MarginExpenseTypesAttributes.Amount,
            MarginExpenseTypesAttributes.ExpenseAs
        ],
        [MarginExpenseTypes.DEA]: [
            MarginExpenseTypesAttributes.IsBillable,
            MarginExpenseTypesAttributes.Amount,
            MarginExpenseTypesAttributes.ExpenseAs
        ],
        [MarginExpenseTypes.REFERRAL_BONUS]: [
            MarginExpenseTypesAttributes.IsBillable,
            MarginExpenseTypesAttributes.Amount,
            MarginExpenseTypesAttributes.Recipient
        ],
        [MarginExpenseTypes.PATIENT_COMPENSATION_FUND]: [
            MarginExpenseTypesAttributes.IsBillable,
            MarginExpenseTypesAttributes.Amount
        ],
        [MarginExpenseTypes.EXPENSE_ALLOWANCE]: [
            MarginExpenseTypesAttributes.IsBillable,
            MarginExpenseTypesAttributes.Amount,
            MarginExpenseTypesAttributes.Cadence
        ],
        [MarginExpenseTypes.BAGGAGE_FEES]: [
            MarginExpenseTypesAttributes.IsBillable,
            MarginExpenseTypesAttributes.Amount
        ],
        [MarginExpenseTypes.OTHER]: [
            MarginExpenseTypesAttributes.IsBillable,
            MarginExpenseTypesAttributes.Amount,
            MarginExpenseTypesAttributes.Specification,
            MarginExpenseTypesAttributes.ExpenseAs
        ]
    };

    private setExpenseCaps(marginExpenses: MarginExpense[], expenseCaps: FacilityExpenseCaps[]) {
        if (!marginExpenses || marginExpenses.length === 0) {
            return;
        }
        const marginExpenseType = marginExpenses[0]?.marginExpenseTypeId;

        if (
            marginExpenseType === MarginExpenseTypes.AIRFARE ||
            marginExpenseType === MarginExpenseTypes.RENTAL_CAR ||
            marginExpenseType === MarginExpenseTypes.HOUSING
        ) {
            const expenseCapsForType = expenseCaps?.find(
                (cap) => cap.paymentTypeId === marginExpenseTypeToPaymentType[marginExpenseType]
            );

            const marginExpense = marginExpenses.find(
                (expense) => expense.marginExpenseAttributeId === MarginExpenseTypesAttributes.ExpenseCap
            );

            if (expenseCapsForType && marginExpense) {
                marginExpense.value = expenseCapsForType.amount ? String(expenseCapsForType.amount) : '0';
            } else if (marginExpense) {
                marginExpense.value = '0';
            }
        }
    }

    private setExpenseAsAttribute(
        expensePayload: MarginExpense[],
        marginExpenseTypeId: MarginExpenseTypes,
        marginId: number
    ) {
        let value: LocumsMarginExpenseAs | null = null;
        const rowId = expensePayload[0]?.rowId;
        if (MarginExpenseReimbursementTypes.includes(marginExpenseTypeId)) {
            value = LocumsMarginExpenseAs.REIMBURSEMENT;
        } else if (MarginExpenseInternalCostTypes.includes(marginExpenseTypeId)) {
            value = LocumsMarginExpenseAs.INTERNAL_COST;
        }

        if (value === null) {
            return;
        }

        expensePayload.push({
            marginExpenseTypeId: marginExpenseTypeId,
            marginExpenseAttributeId: MarginExpenseTypesAttributes.ExpenseAs,
            marginId: marginId,
            value: value,
            isDeleted: false,
            rowId: rowId
        });
    }
}
